import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearCredentials, setUserName } from "../../store/authSlice";
import { getUser } from "../../services/api";

const Icon = ({ path, ...props }) => (
  <svg fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const ICONS = {
  grid:   "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
  list:   "M4 6h16M4 10h16M4 14h16M4 18h16",
  person: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  vault:  "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
  doc:    "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  logout: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1",
};

const RECEIVER_NAV = [
  { label: "Invoice List",  icon: "grid",   to: "/receiver/overview" },
  { label: "Transactions", icon: "list",   to: "/receiver/transactions" },
  { label: "Senders",      icon: "person", to: "/receiver/senders" },
  { label: "Treasury",     icon: "vault",  to: "/receiver/treasury" },
];

const SENDER_NAV = [
  { label: "Invoice List",  icon: "doc",    to: "/sender/invoices" },
  { label: "Transactions", icon: "list",   to: "/sender/transactions" },
  { label: "Treasury",     icon: "vault",  to: "/sender/treasury" },
  { label: "OTC Requests", icon: "person", to: "/sender/otc-requests" },
];

const SEGMENT_MAP = {
  overview:      "Invoice Overview",
  invoices:      "Invoices",
  transactions:  "Transactions",
  senders:       "Senders",
  treasury:      "Treasury",
  "otc-requests": "OTC Requests",
  "add-bank":    "Add Bank Account",
  pay:           "Pay Invoice",
  new:           "New Transaction",
  fund:          "Fund Wallet",
};

const isUuid = (s) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

// When a breadcrumb segment doesn't map to a real route, override the target.
// e.g. receiver has no /receiver/invoices list page — its list lives at /receiver/overview.
const BREADCRUMB_PATH_OVERRIDES = {
  receiver: { invoices: "/receiver/overview" },
  sender:   {},
};

const Breadcrumb = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const allParts = pathname.split("/").filter(Boolean);
  const role = allParts[0]; // "sender" | "receiver"
  const parts = allParts.slice(1); // skip role

  // Only render on 2nd-level pages (depth > 1)
  if (parts.length <= 1) return null;

  const roleOverrides = BREADCRUMB_PATH_OVERRIDES[role] ?? {};

  const crumbs = parts.map((p, i) => {
    let label;
    if (p === "create") {
      const parent = parts[i - 1];
      label = parent === "senders" ? "Create Sender" : "Create Invoice";
    } else if (isUuid(p)) {
      // Context-aware label for UUID segments
      const parent = parts[i - 1];
      if (parent === "senders") label = "View Details";
      else if (parent === "invoices") {
        // If there is a segment after the UUID (e.g. "pay"), drop the UUID crumb
        // entirely so the breadcrumb reads "Invoices / Pay Invoice" instead of
        // "Invoices / <UUID> / Pay Invoice".
        if (i < parts.length - 1) return null;
        // Last segment — show a friendly label instead of the raw UUID
        label = "Invoice Details";
      } else if (parent === "transactions") {
        label = "Transaction Detail";
      } else if (parent === "otc-requests") {
        label = "OTC Request Details";
      } else {
        label = p.slice(0, 8).toUpperCase();
      }
    } else {
      label =
        SEGMENT_MAP[p] ||
        p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    }
    const rawPath = `/${role}/${parts.slice(0, i + 1).join("/")}`;
    // Use role-specific override if defined, otherwise use the natural path
    const path = roleOverrides[p] ?? rawPath;
    return { label, path };
  }).filter(Boolean);

  return (
    <nav className="flex items-center gap-1.5 text-sm mb-5">
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-gray-300">/</span>}
          {i < crumbs.length - 1 ? (
            <button
              onClick={() => navigate(c.path)}
              className="text-gray-400 hover:text-brand transition-colors"
            >
              {c.label}
            </button>
          ) : (
            <span className="text-gray-700 font-medium">{c.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

// Derive initials from a display name string
const nameToInitials = (name) => {
  if (!name) return null;
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

const AppShell = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector((s) => s.auth.role);
  const userId = useSelector((s) => s.auth.userId);
  const userName = useSelector((s) => s.auth.userName);
  const quotePermissions = useSelector((s) => s.auth.quotePermissions);
  const navItems = useMemo(() => {
    const base = role === "receiver" ? RECEIVER_NAV : SENDER_NAV;
    // const hasWallet = quotePermissions.canOfframp || quotePermissions.canOnramp;
    // keeping it as a source of truth to show the treasury tab
    const hasWallet = quotePermissions.canViewTreasury || false;
    return hasWallet ? base : base.filter((item) => item.label !== "Treasury");
  }, [role, quotePermissions]);

  // Derive initials synchronously from the cached name — no flicker
  const initials = useMemo(() => nameToInitials(userName) || "?", [userName]);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Only fetch from API if the name hasn't been cached yet
  useEffect(() => {
    if (!userId || userName) return;
    getUser(userId)
      .then((res) => {
        const name = res?.data?.name || res?.data?.firstName || res?.name || "";
        if (name) dispatch(setUserName(name));
      })
      .catch(() => {});
  }, [userId, userName, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    dispatch(clearCredentials());
    navigate("/auth/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* ── Sidebar ── */}
      <aside className="w-48 flex-shrink-0 bg-white border-r border-gray-100 flex flex-col">
        {/* Logo */}
        <div className="px-5 py-4 border-b border-gray-100">
          <img src="/borderlessLogo.svg" alt="Borderless" className="h-6" />
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 mx-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? "bg-blue-50 text-brand"
                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    path={ICONS[item.icon]}
                    className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-brand" : "text-gray-400"}`}
                  />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-14 flex-shrink-0 bg-white border-b border-gray-100 flex items-center justify-end px-6">
          {/* Profile dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="w-8 h-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0 select-none hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-brand/40"
              title="Account"
            >
              {initials}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg py-1.5 z-50">
                <button
                  onClick={() => { setMenuOpen(false); navigate("/account/change-password"); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                  </svg>
                  Change Password
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Icon path={ICONS.logout} className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Breadcrumb />
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppShell;
